import { useMemo, useState } from 'react';
import type { WarehouseInfo } from '@econtractor/types';
import type { OffTransferShow } from 'modules/warehouse';
import { useSettings } from 'context/SettingsContext';
import vconst from 'utils/helpers/structs/vconst';
import { useObjectCard } from '../../../context/ObjectCardProvider';

export type AddProductsSourceKey = 'reserve' | 'team' | 'main';

const useAddProductsTransfer = () => {
    const { vObject, fetchObject, isEditLocked } = useObjectCard();
    const { localSettings } = useSettings();

    const [showTransfer, setShowTransfer] = useState<OffTransferShow>(false);
    const [sourceWId, setSourceWId] = useState<number>(0);
    const [sourceReservObjectId, setSourceReservObjectId] = useState<number | null>(null);

    const objectWarehouseId = Number(vObject?.warehouseId || 0);
    const reservWarehouseId = Number(vObject?.reservWarehouseId || 0);
    const teamId = Number(vObject?.object?.teamId || 0);
    const teamName = String(vObject?.object?.teamName || '');
    const objectId = Number(vObject?.object?.ID || 0);

    const teamWarehouseId = useMemo(() => {
        if (!teamId) return 0;
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        const teamWh = warehouses.find(
            (wh) =>
                Number(wh.teamId) === teamId &&
                !Number(wh.objectId) &&
                !Number(wh.reservObjectId),
        );
        return Number(teamWh?.ID || 0);
    }, [teamId, localSettings?.WAREHOUSES]);

    const sources = useMemo(
        () =>
            [
                {
                    key: 'reserve' as const,
                    warehouseId: reservWarehouseId,
                    available: reservWarehouseId > 0,
                    reservObjectId: objectId || null,
                },
                {
                    key: 'team' as const,
                    warehouseId: teamWarehouseId,
                    available: teamWarehouseId > 0,
                    reservObjectId: null,
                },
                {
                    key: 'main' as const,
                    warehouseId: Number(vconst.waherouseMainId),
                    available: true,
                    reservObjectId: null,
                },
            ].filter((s) => s.available && s.warehouseId > 0),
        [reservWarehouseId, teamWarehouseId, objectId],
    );

    const canAdd =
        !isEditLocked && objectWarehouseId > 0 && sources.length > 0;

    const openTransfer = (key: AddProductsSourceKey, productId?: number) => {
        const source = sources.find((s) => s.key === key);
        if (!source) return;
        setSourceWId(source.warehouseId);
        setSourceReservObjectId(source.reservObjectId);
        const preselectId = Number(productId || 0);
        setShowTransfer(preselectId > 0 ? preselectId : true);
    };

    const handleFetchStocks = async () => {
        await fetchObject();
    };

    return {
        canAdd,
        sources,
        teamName,
        objectWarehouseId,
        showTransfer,
        setShowTransfer,
        sourceWId,
        sourceReservObjectId,
        openTransfer,
        handleFetchStocks,
    };
};

export default useAddProductsTransfer;
